<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime, getCurrentUserLocale } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  canReadResource,
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import ContactInfoRow from './ContactInfoRow.vue';
import DashboardIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import SocialIcons, {
  SOCIAL_MEDIA_LINKS,
  socialProfilesOf,
} from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import NewConversation from './NewConversation.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ImportContactsWithLabelsModal from 'dashboard/components/widgets/uazapi/ImportContactsWithLabelsModal.vue';
import ContactsAPI from 'dashboard/api/contacts';
import { getCountryFlag } from 'dashboard/helper/flag';
import { parseAPIErrorMessage } from 'dashboard/helper/APIHelper';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';

export default {
  components: {
    ContactInfoRow,
    DashboardIcon,
    EditContact,
    Thumbnail,
    SocialIcons,
    NewConversation,
    ContactMergeModal,
    ImportContactsWithLabelsModal,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    closeIconName: {
      type: String,
      default: 'chevron-right',
    },
    isContactPanelDetached: {
      type: Boolean,
      default: false,
    },
    showDetachButton: {
      type: Boolean,
      default: true,
    },
    // Modo "dentro de uma PanelSection", usado só pela aba de contato do painel
    // da conversa (#1243). Ele muda duas coisas, e as duas valem só ali:
    //
    // 1. Sem moldura própria — quem embrulha já dá o respiro. O `p-5` daqui
    //    somava ao padding da `PanelSection` e desalinhava as linhas do contato
    //    em relação ao título da seção.
    // 2. Os dados saem como linhas rótulo/valor separadas por divisória, em
    //    vez dos `ContactInfoRow` com ícone, e a fileira de ações ganha no fim
    //    um atalho para a página do contato ("Abrir contato"). A fileira de
    //    ícones vale nos dois modos desde `852ab6d5` — o ramo com dois botões
    //    de texto e menu `⋮` que existia aqui foi apagado.
    //
    // Montam este componente em modo `compact` o painel da conversa e, desde a
    // #1652, o `ContactInfoPanel` (lista de Contatos, tela do contato, perfil da
    // empresa e Kanban).
    compact: {
      type: Boolean,
      default: false,
    },
    // `compact` liga duas coisas de uma vez: as linhas rotulo/valor e o atalho
    // "Abrir contato". Na tela do proprio contato (#1652) so a primeira faz
    // sentido -- o atalho levaria a pagina em que o usuario ja esta.
    hideProfileLink: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { isAdmin } = useAdmin();
    return {
      isAdmin,
    };
  },
  data() {
    return {
      showEditModal: false,
      showConversationModal: false,
      showMergeModal: false,
      showDeleteModal: false,
      showImportGroupContactsModal: false,
      isImportingGroupContacts: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
      currentChat: 'getSelectedChat',
    }),
    // O locale nao e opcional: o default do `dynamicTime` e 'en', entao sem
    // ele a dica saia "Criado 9 days ago" num sistema em portugues.
    createdAtLabel() {
      return `${this.$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${
        this.createdAtValue
      }`;
    },
    // So o tempo, sem o "Criado" na frente: na lista de dados a coluna da
    // esquerda ja e o rotulo, e a dica do modo com avatar remonta a frase.
    createdAtValue() {
      if (!this.contact.created_at) {
        return '';
      }
      return dynamicTime(this.contact.created_at, getCurrentUserLocale(this));
    },
    userPermissions() {
      return getUserPermissions(this.currentUser, this.currentAccountId);
    },
    // Nova conversa a partir do contato precisa das caixas dele
    // (`contacts/:id/contactable_inboxes`), que cobram a leitura de contato.
    // Sem ela o modal abria sem caixa nenhuma para escolher (#1625).
    canStartConversation() {
      return canReadResource('contacts', this.userPermissions);
    },
    canEditContact() {
      const isAdminUser =
        typeof this.isAdmin === 'boolean' ? this.isAdmin : this.isAdmin?.value;
      return (
        isAdminUser ||
        hasPermissions(['contact_edit', 'contact_manage'], this.userPermissions)
      );
    },
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    companyName() {
      const companyName = this.additionalAttributes.company_name;
      return typeof companyName === 'string' ? companyName.trim() : '';
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      return socialProfilesOf(this.contact);
    },
    contactDisplayName() {
      return this.contact.name || this.$t('EDIT_CONTACT.UNNAMED');
    },
    contactPanelDetachToggleText() {
      return this.isContactPanelDetached
        ? this.$t('CONVERSATION.HEADER.ATTACH_DETAILS')
        : this.$t('CONVERSATION.HEADER.DETACH_DETAILS');
    },
    contactPanelDetachToggleIcon() {
      return this.isContactPanelDetached
        ? 'lock-open-outline'
        : 'lock-closed-outline';
    },
    hasRequiredContactField() {
      const socialProfiles = this.additionalAttributes.social_profiles || {};
      return Boolean(
        this.contact.email ||
          this.contact.phone_number ||
          this.contact.identifier ||
          Object.values(socialProfiles).some(value => value)
      );
    },
    // As linhas de dado do modo `compact`, em vez dos `ContactInfoRow` com
    // ícone: rótulo à esquerda, valor à direita. Uma linha sem valor não some —
    // ela diz "Não disponível", que é a informação de que o campo está vazio.
    detailRows() {
      const socialRows = SOCIAL_MEDIA_LINKS.filter(
        // O WhatsApp fica de fora: ele não é um perfil cadastrado, é o próprio
        // telefone que já está duas linhas acima, e a seção "Perfis sociais"
        // dá o atalho para o `wa.me`. A linha só repetia o mesmo número num
        // terceiro formato.
        media => media.key !== 'phone' && !!this.socialProfiles[media.key]
      ).map(media => {
        const profile = this.socialProfiles[media.key];

        return {
          key: media.key,
          label: media.label,
          value: profile,
          href: `${media.link}${profile}`,
          external: true,
        };
      });

      return [
        // Sem `href`: o clique aqui COPIA, como nos cartoes do Kanban. Os dois
        // gestos nao cabem no mesmo alvo, e copiar e o que o atendente faz com
        // e-mail e telefone o dia inteiro -- o `mailto:`/`tel:` do painel
        // dependia de ter cliente de e-mail ou softphone configurado na
        // maquina.
        {
          key: 'email',
          label: this.$t('CONTACT_PANEL.FIELDS.EMAIL'),
          value: this.contact.email,
          copyable: true,
        },
        {
          key: 'phone',
          label: this.$t('CONTACT_PANEL.FIELDS.PHONE'),
          value: this.contact.phone_number,
          copyable: true,
        },
        ...socialRows,
        {
          key: 'identifier',
          label: this.$t('CONTACT_PANEL.IDENTIFIER'),
          // O campo `identifier` do contato, e nada mais: sem `href` e sem
          // `copyable`, porque aqui o dado é informação, não atalho.
          value: this.contact.identifier,
          // O identificador é dado técnico (o id do contato no canal), não algo
          // que o atendente lê para falar com alguém: o desenho o mantém na
          // lista, mas um tom abaixo dos campos de contato de verdade.
          dimmed: true,
        },
        {
          key: 'company',
          label: this.$t('CONTACT_PANEL.COMPANY'),
          value: this.companyName,
          optional: true,
        },
        {
          key: 'location',
          label: this.$t('CONTACT_PANEL.LOCATION'),
          value: this.location || this.additionalAttributes.location,
          optional: true,
        },
        // Ultima da lista de proposito: e dado sobre o CADASTRO, nao sobre como
        // falar com a pessoa. Estava numa dica de ferramenta pendurada no nome
        // do contato, onde so achava quem passasse o mouse por cima.
        {
          key: 'created_at',
          // Chave propria, e nao a `CREATED_AT_LABEL` da dica do modo com
          // avatar: la a frase e montada inteira ("Criado ha 9 dias") e um
          // "em" no meio dela sairia errado.
          label: this.$t('CONTACT_PANEL.FIELDS.CREATED_AT'),
          value: this.createdAtValue,
          dimmed: true,
          optional: true,
        },
      ].filter(row => row.value || !row.optional);
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contactDisplayName}?`;
    },
    isConversationContactContext() {
      return (
        Boolean(this.currentChat?.id) &&
        (isAConversationRoute(this.$route.name, true) ||
          isAInboxViewRoute(this.$route.name, true))
      );
    },
    canToggleContactBlock() {
      return Boolean(this.contact?.id);
    },
    isContactBlocked() {
      if (this.isConversationContactContext) {
        return Boolean(this.currentChat?.muted);
      }
      return Boolean(this.contact?.blocked);
    },
    // O grupo que existe como Contact sem conversa (issue #492) só é reconhecível
    // pelo contact_inbox cujo source_id é o JID completo do grupo — é assim que
    // GroupsImportService grava um grupo importado da listagem "todos os grupos".
    uazapiGroupContactInbox() {
      const contactInboxes = this.contact.contact_inboxes || [];
      return contactInboxes.find(
        contactInbox =>
          contactInbox.source_id?.endsWith('@g.us') &&
          contactInbox.inbox?.channel_type === 'Channel::Whatsapp' &&
          contactInbox.inbox?.provider === 'uazapi'
      );
    },
    canImportGroupContacts() {
      return (
        Boolean(this.uazapiGroupContactInbox) &&
        hasPermissions(
          ['administrator', 'agent', 'contact_create', 'contact_manage'],
          this.userPermissions
        )
      );
    },
  },
  methods: {
    async copyValue(value) {
      await copyTextToClipboard(value);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    toggleMergeModal() {
      this.showMergeModal = !this.showMergeModal;
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    onPanelToggle() {
      this.$emit('togglePanel');
    },
    onContactPanelDetachToggle() {
      this.$emit('contactPanelDetachToggle');
    },
    toggleConversationModal() {
      this.showConversationModal = !this.showConversationModal;
      this.$emitter.emit(
        BUS_EVENTS.NEW_CONVERSATION_MODAL,
        this.showConversationModal
      );
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    async muteConversation() {
      await this.$store.dispatch('muteConversation', this.currentChat.id);
      useAlert(this.$t('CONTACT_PANEL.MUTED_SUCCESS'));
    },
    async unmuteConversation() {
      await this.$store.dispatch('unmuteConversation', this.currentChat.id);
      useAlert(this.$t('CONTACT_PANEL.UNMUTED_SUCCESS'));
    },
    async blockContact() {
      await this.$store.dispatch('contacts/update', {
        id: this.contact.id,
        blocked: true,
      });
      useAlert(this.$t('CONTACT_PANEL.MUTED_SUCCESS'));
    },
    async unblockContact() {
      await this.$store.dispatch('contacts/update', {
        id: this.contact.id,
        blocked: false,
      });
      useAlert(this.$t('CONTACT_PANEL.UNMUTED_SUCCESS'));
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showConversationModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        const countryFlag = countryCode ? getCountryFlag(countryCode) : '🌎';
        return `${cityAndCountry} ${countryFlag}`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/deleteContact', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          parseAPIErrorMessage(error) ||
            this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    openMergeModal() {
      this.toggleMergeModal();
    },
    openImportGroupContactsModal() {
      this.showImportGroupContactsModal = true;
    },
    closeImportGroupContactsModal() {
      this.showImportGroupContactsModal = false;
    },
    async importGroupContacts(labels = []) {
      this.isImportingGroupContacts = true;
      try {
        const response = await ContactsAPI.importUazapiGroupContacts(
          this.contact.id,
          labels
        );
        useAlert(response.data.message);
        this.closeImportGroupContactsModal();
      } catch (error) {
        useAlert(
          error.response?.data?.message ||
            this.$t('UAZAPI_IMPORT.GROUP_CONTACTS.ERROR')
        );
      } finally {
        this.isImportingGroupContacts = false;
      }
    },
  },
};
</script>

<template>
  <div>
    <div class="relative items-center w-full" :class="compact ? 'p-0' : 'p-5'">
      <div class="flex flex-col w-full gap-4 text-left rtl:text-right">
        <!-- A faixa some quando nao ha nada nela: com altura zero ela ainda
             cobrava o `gap-4` do pai, e eram 16px entre o titulo da secao e a
             primeira linha de dado no painel da conversa. -->
        <div
          v-if="showAvatar || showDetachButton || showCloseButton"
          class="flex flex-row justify-between items-start"
        >
          <Thumbnail
            v-if="showAvatar"
            :src="contact.thumbnail"
            size="72px"
            :username="contactDisplayName"
            :status="contact.availability_status"
          />
          <div class="contact-panel-header-actions flex items-center gap-1">
            <button
              v-if="showDetachButton"
              v-tooltip.left="contactPanelDetachToggleText"
              type="button"
              class="contact-panel-detach-toggle"
              :aria-label="contactPanelDetachToggleText"
              :aria-pressed="isContactPanelDetached.toString()"
              @click="onContactPanelDetachToggle"
            >
              <DashboardIcon :icon="contactPanelDetachToggleIcon" size="18" />
            </button>
            <woot-button
              v-if="showCloseButton"
              :icon="closeIconName"
              class="clear secondary rtl:rotate-180"
              @click="onPanelToggle"
            />
          </div>
        </div>

        <div class="flex flex-col items-start gap-2 min-w-0 w-full">
          <div v-if="showAvatar" class="flex items-start w-full min-w-0 gap-2">
            <h3
              class="flex-shrink max-w-full min-w-0 my-0 text-lg font-semibold capitalize break-words text-body"
            >
              {{ contactDisplayName }}
            </h3>
            <div class="flex flex-row items-center gap-1">
              <fluent-icon
                v-if="contact.created_at"
                v-tooltip.left="createdAtLabel"
                icon="info"
                size="14"
                class="mt-0.5"
              />
              <a
                :href="contactProfileLink"
                class="text-base"
                target="_blank"
                rel="noopener nofollow noreferrer"
              >
                <woot-button
                  size="tiny"
                  icon="open"
                  variant="clear"
                  color-scheme="secondary"
                />
              </a>
            </div>
          </div>

          <p v-if="additionalAttributes.description" class="break-words mb-0.5">
            {{ additionalAttributes.description }}
          </p>
          <div
            v-if="!hasRequiredContactField"
            v-tooltip="$t('CONTACT_PANEL.MISSING_REQUIRED_FIELD_HELP')"
            class="flex items-center gap-2 px-2 py-1 rounded-md text-xs border border-yellow-500 bg-yellow-200/60 dark:border-yellow-700 dark:bg-yellow-200/20"
          >
            <fluent-icon
              icon="info"
              size="14"
              class="text-yellow-700 dark:text-yellow-400"
            />
            <span class="text-yellow-700 dark:text-yellow-400">
              {{ $t('CONTACT_PANEL.MISSING_REQUIRED_FIELD') }}
            </span>
          </div>
          <div v-if="!compact" class="flex flex-col items-start w-full gap-2">
            <ContactInfoRow
              :href="contact.email ? `mailto:${contact.email}` : ''"
              :value="contact.email"
              icon="mail"
              emoji="✉️"
              :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
              show-copy
            />
            <ContactInfoRow
              :href="contact.phone_number ? `tel:${contact.phone_number}` : ''"
              :value="contact.phone_number"
              icon="call"
              emoji="📞"
              :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
              show-copy
            />
            <ContactInfoRow
              v-if="contact.identifier"
              :value="contact.identifier"
              icon="contact-identify"
              emoji="🪪"
              :title="$t('CONTACT_PANEL.IDENTIFIER')"
            />
            <ContactInfoRow
              v-if="companyName"
              :value="companyName"
              icon="building-bank"
              emoji="🏢"
              :title="$t('CONTACT_PANEL.COMPANY')"
            />
            <ContactInfoRow
              v-if="location || additionalAttributes.location"
              :value="location || additionalAttributes.location"
              icon="map"
              emoji="🌍"
              :title="$t('CONTACT_PANEL.LOCATION')"
            />
            <SocialIcons :social-profiles="socialProfiles" />
          </div>

          <!-- `dl` porque cada linha e um par rotulo/valor; o `m-0` do `dd` tira
               a margem de 40px que o navegador poe por padrao. -->
          <!-- `divide-y` em vez de `gap`: o desenho separa um campo do outro com
               uma linha, e ela precisa nascer ENTRE as linhas -- borda em cada
               item deixaria um traco solto embaixo do ultimo, colado nos
               botoes. -->
          <dl
            v-else
            class="flex flex-col w-full m-0 divide-y divide-line-faint"
          >
            <div
              v-for="row in detailRows"
              :key="row.key"
              class="flex items-center justify-between w-full gap-3 py-1.5 first:pt-0 last:pb-0"
            >
              <dt class="text-xs text-muted shrink-0">{{ row.label }}</dt>
              <dd class="flex items-center min-w-0 gap-1.5 m-0">
                <!-- Campo copiavel: o alvo do clique e o VALOR, nao um icone
                     de 14px ao lado dele. E o mesmo gesto dos cartoes do
                     Kanban (`KanbanCard.vue`), de onde vem tambem o azul do
                     hover e o encolhimento no clique. -->
                <button
                  v-if="row.copyable && row.value"
                  type="button"
                  class="flex items-center min-w-0 gap-1.5 copyable-item"
                  :title="$t('CONTACT_PANEL.COPY')"
                  @click="copyValue(row.value)"
                >
                  <span class="text-xs truncate copyable-text text-body">
                    {{ row.value }}
                  </span>
                  <fluent-icon icon="clipboard" size="14" class="shrink-0" />
                </button>
                <a
                  v-else-if="row.href && row.value"
                  :href="row.href"
                  :target="row.external ? '_blank' : undefined"
                  :rel="
                    row.external ? 'noopener nofollow noreferrer' : undefined
                  "
                  :title="row.value"
                  class="text-xs truncate text-body hover:underline"
                >
                  {{ row.value }}
                </a>
                <span
                  v-else-if="row.value"
                  :title="row.value"
                  class="text-xs truncate"
                  :class="row.dimmed ? 'text-muted' : 'text-body'"
                >
                  {{ row.value }}
                </span>
                <span v-else class="text-xs text-muted">
                  {{ $t('CONTACT_PANEL.FIELDS.EMPTY') }}
                </span>
              </dd>
            </div>
          </dl>
        </div>
        <!-- Centralizada só no painel: na página de contatos a fileira segue a
             coluna de texto à esquerda, como sempre esteve. -->
        <div
          class="flex items-center w-full gap-2"
          :class="compact ? 'justify-center' : ''"
        >
          <woot-button
            v-if="canStartConversation"
            v-tooltip="$t('CONTACT_PANEL.NEW_MESSAGE')"
            :title="$t('CONTACT_PANEL.NEW_MESSAGE')"
            icon="chat"
            size="small"
            @click="toggleConversationModal"
          />
          <woot-button
            v-if="canEditContact"
            v-tooltip="$t('EDIT_CONTACT.BUTTON_LABEL')"
            :title="$t('EDIT_CONTACT.BUTTON_LABEL')"
            icon="edit"
            variant="smooth"
            size="small"
            @click="toggleEditModal"
          />
          <woot-button
            v-tooltip="$t('CONTACT_PANEL.MERGE_CONTACT')"
            :title="$t('CONTACT_PANEL.MERGE_CONTACT')"
            icon="merge"
            variant="smooth"
            size="small"
            color-scheme="secondary"
            :disabled="uiFlags.isMerging"
            @click="openMergeModal"
          />
          <woot-button
            v-if="canToggleContactBlock && !isContactBlocked"
            v-tooltip="$t('CONTACT_PANEL.MUTE_CONTACT')"
            :title="$t('CONTACT_PANEL.MUTE_CONTACT')"
            icon="speaker-mute"
            variant="smooth"
            size="small"
            color-scheme="secondary"
            :disabled="uiFlags.isUpdating"
            @click="
              isConversationContactContext ? muteConversation() : blockContact()
            "
          />
          <woot-button
            v-else-if="canToggleContactBlock"
            v-tooltip="$t('CONTACT_PANEL.UNMUTE_CONTACT')"
            :title="$t('CONTACT_PANEL.UNMUTE_CONTACT')"
            icon="speaker-1"
            variant="smooth"
            size="small"
            color-scheme="secondary"
            :disabled="uiFlags.isUpdating"
            @click="
              isConversationContactContext
                ? unmuteConversation()
                : unblockContact()
            "
          />
          <woot-button
            v-if="isAdmin"
            v-tooltip="$t('DELETE_CONTACT.BUTTON_LABEL')"
            :title="$t('DELETE_CONTACT.BUTTON_LABEL')"
            icon="delete"
            variant="smooth"
            size="small"
            color-scheme="alert"
            :disabled="uiFlags.isDeleting"
            @click="toggleDeleteModal"
          />
          <woot-button
            v-if="canImportGroupContacts"
            v-tooltip="$t('UAZAPI_IMPORT.GROUP_CONTACTS.TITLE')"
            :title="$t('UAZAPI_IMPORT.GROUP_CONTACTS.TITLE')"
            icon="person-add"
            variant="smooth"
            size="small"
            color-scheme="secondary"
            @click="openImportGroupContactsModal"
          />
          <!-- Só no painel da conversa: na página de contatos o perfil já é a
               própria página, e o botão levaria a lugar nenhum. -->
          <a
            v-if="compact && !hideProfileLink"
            v-tooltip="$t('CONTACT_PANEL.VIEW_PROFILE')"
            :href="contactProfileLink"
            target="_blank"
            rel="noopener nofollow noreferrer"
          >
            <woot-button
              icon="open"
              variant="smooth"
              size="small"
              color-scheme="secondary"
              :title="$t('CONTACT_PANEL.VIEW_PROFILE')"
            />
          </a>
        </div>
      </div>
    </div>
    <!-- Modais ficam fora do container do painel para nao herdar stacking context -->
    <EditContact
      v-if="showEditModal"
      :show="showEditModal"
      :contact="contact"
      @cancel="toggleEditModal"
    />
    <NewConversation
      v-if="contact.id && canStartConversation"
      :show="showConversationModal"
      :contact="contact"
      @cancel="toggleConversationModal"
    />
    <ContactMergeModal
      v-if="showMergeModal"
      :primary-contact="contact"
      :show="showMergeModal"
      @close="toggleMergeModal"
    />
    <woot-delete-modal
      v-if="showDeleteModal"
      :show.sync="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
    <ImportContactsWithLabelsModal
      :show="showImportGroupContactsModal"
      :title="$t('UAZAPI_IMPORT.GROUP_CONTACTS.TITLE')"
      :description="$t('UAZAPI_IMPORT.GROUP_CONTACTS.DESCRIPTION')"
      :is-importing="isImportingGroupContacts"
      @confirm="importGroupContacts"
      @close="closeImportGroupContactsModal"
    />
  </div>
</template>

<style lang="scss" scoped>
/* Campo copiavel, igual ao do Kanban (`KanbanCard.vue`): tinge de azul ao
   passar o mouse e encolhe no clique. O recuo negativo cancela o respiro que o
   fundo do hover acrescenta, para o valor continuar alinhado com a borda
   direita da linha. */
.copyable-item {
  @apply px-1 -mx-1 py-0.5 -my-0.5 rounded transition-all duration-200
    cursor-pointer text-subtle border border-transparent;
}
.copyable-item:hover {
  @apply bg-brand/10 border-brand/30 text-brand-text;
}
.copyable-item:hover .copyable-text {
  @apply font-semibold text-brand-text;
}
.copyable-item:active {
  @apply scale-[.98] bg-brand/20;
}

.contact-panel-detach-toggle {
  display: none;
}

@media (min-width: 769px) {
  .contact-panel-detach-toggle {
    align-items: center;
    background: transparent;
    border: 0;
    color: #111827;
    cursor: pointer;
    display: inline-flex;
    flex-shrink: 0;
    height: 2rem;
    justify-content: center;
    padding: 0;
    transition:
      color 160ms ease,
      opacity 160ms ease,
      transform 160ms ease;
    width: 2rem;

    &:hover,
    &:focus-visible {
      color: #000000;
      opacity: 1;
      transform: scale(1.12);
    }

    &:focus-visible {
      outline: 2px solid rgba(0, 123, 255, 0.45);
      outline-offset: 2px;
    }
  }

  .dark .contact-panel-detach-toggle {
    color: #f8fafc;

    &:hover,
    &:focus-visible {
      color: #ffffff;
    }
  }
}
</style>
